import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import './AuctionFormationTab.css';
import {
  Bandage, RefreshCw, AlertTriangle, PieChart as PieChartIcon, Users, TrendingUp, TrendingDown,
} from 'lucide-react';
import TeamBadge from '../TeamBadge';
import TeamNextFixtures from '../TeamNextFixtures';
import AppTooltip from '../Tooltip';
import { useTheme } from '../../context/ThemeContext';
import { getAllPlayerRatingsMap } from '../../firebase/playerService';
import { ROLE_ORDER, ROLE_LABELS, fmt, calcPlayerScore, getPlayProbabilityLabel } from './auctionCommon';
import { computeRecentFormDelta } from './auctionAnalysis';
import {
  MANTRA_PRESETS,
  MANTRA_ROW_ORDER,
  MANTRA_TOKEN_PATTERN,
  MANTRA_ROLE_THEME,
} from './MantraRoleConfiguration';
import { simulateAbsence, MODE_LABELS } from './MantraSubstitutionEngine';

// Peso del segnale "forma recente" (vedi computeRecentFormDelta) sul punteggio
// usato per scegliere l'undici: stessa scala dei pesi di calcPlayerScore
// (fmarkavg pesa ~3), clampato per non far ribaltare la scelta solo per 1-2
// giornate fortunate/sfortunate.
const RECENT_FORM_SCORE_WEIGHT = 3.5;
const RECENT_FORM_CLAMP = 2;

// Penalità di disponibilità: molto più marcate del ±15% di
// getPlayProbabilityScoreFactor (calcPlayerScore), che è volutamente tenue
// perché pesa su TUTTI gli usi del punteggio (prezzi, PMA...). Qui invece la
// domanda è "chi schiero QUESTA giornata": un infortunato va escluso quasi
// sempre, un dubbio dalle probabili formazioni pesa ma non è definitivo.
const AVAILABILITY_INJURY_PENALTY = 50;
const AVAILABILITY_BENCH_PENALTY = 8;

// Aggiusta il punteggio base (calcPlayerScore) con i segnali disponibili per
// QUESTA giornata specifica: forma recente (ratingsById) e disponibilità
// reale — infortuni/probabili formazioni (availabilityById). Entrambi
// opzionali e indipendenti: il tab Formazione passa solo ratingsById (toggle
// "Forma recente"), la Panoramica squadra passa anche availabilityById per
// una scelta più informata.
function applyMatchdayAdjustments(score, purchase, { ratingsById = null, availabilityById = null } = {}) {
  let next = score;
  let formDelta = null;
  let availabilityFlag = null;

  const form = ratingsById ? computeRecentFormDelta(purchase, ratingsById) : null;
  if (form) {
    const clamped = Math.max(-RECENT_FORM_CLAMP, Math.min(RECENT_FORM_CLAMP, form.delta));
    next += clamped * RECENT_FORM_SCORE_WEIGHT;
    formDelta = form.delta;
  }

  if (availabilityById) {
    const id = String(purchase?.playerId ?? purchase?.id ?? '');
    const avail = typeof availabilityById.get === 'function' ? availabilityById.get(id) : availabilityById[id];
    if (avail?.injury) {
      next -= AVAILABILITY_INJURY_PENALTY;
      availabilityFlag = { kind: 'injury', ...avail.injury };
    } else if (avail?.lineup?.status === 'bench') {
      next -= AVAILABILITY_BENCH_PENALTY;
      availabilityFlag = { kind: 'bench', ...avail.lineup };
    }
  }

  return { score: next, formDelta, availabilityFlag };
}

const MODE_DESCRIPTIONS = {
  basic: 'Prova prima senza cambiare modulo; se serve cambia modulo; solo come ultima opzione accetta un ruolo assimilato (malus −1).',
  easy: 'Il modulo non cambia mai: o trovi lo stesso ruolo, o uno assimilato con malus −1.',
  master: 'Punta a schierare più titolari possibile: entra chi è più alto in panchina, anche cambiando modulo o con malus.',
};

const CLASSIC_PRESETS = {
  '3-5-2': { P: 1, D: 3, C: 5, A: 2 },
  '3-4-3': { P: 1, D: 3, C: 4, A: 3 },
  '4-3-3': { P: 1, D: 4, C: 3, A: 3 },
  '4-4-2': { P: 1, D: 4, C: 4, A: 2 },
  '4-5-1': { P: 1, D: 4, C: 5, A: 1 },
  '5-3-2': { P: 1, D: 5, C: 3, A: 2 },
  '5-4-1': { P: 1, D: 5, C: 4, A: 1 },
};

export const CLASSIC_OPTIONS = ['3-5-2', '3-4-3', '4-3-3', '4-4-2', '4-5-1', '5-3-2', '5-4-1'];

export const MANTRA_OPTIONS = ['343', '3412', '3421', '352', '3511', '433', '4312', '442', '4141', '4411', '4231'];


export function normalizeFormationKey(value = '4-3-3', rolesMode = 'classic') {
  const raw = String(value || '').trim();

  if (rolesMode === 'mantra') {
    return MANTRA_PRESETS[raw] ? raw : '343';
  }

  if (CLASSIC_PRESETS[raw]) return raw;

  const compact = raw.replace(/[^0-9]/g, '');
  const aliasMap = {
    352: '3-5-2',
    343: '3-4-3',
    433: '4-3-3',
    442: '4-4-2',
    451: '4-5-1',
    532: '5-3-2',
    541: '5-4-1',
  };

  return aliasMap[compact] || '4-3-3';
}

function normalizeMantraToken(token = '') {
  const cleaned = String(token).trim().replace(/\s+/g, '');
  if (!cleaned) return null;

  const key = cleaned.toUpperCase();
  if (key === 'PC') return 'Pc';
  if (key === 'DD') return 'Dd';
  if (key === 'DC') return 'Dc';
  if (key === 'DS') return 'Ds';
  if (key === 'P') return 'P';
  if (key === 'E') return 'E';
  if (key === 'M') return 'M';
  if (key === 'C') return 'C';
  if (key === 'W') return 'W';
  if (key === 'T') return 'T';
  if (key === 'A') return 'A';
  if (key === 'B') return 'B';

  return null;
}

function splitRoleTokens(value = '') {
  const raw = String(value || '').trim();
  if (!raw) return [];

  const unique = [];
  raw.split(/[\/,|+]/g).forEach((segment) => {
    const part = String(segment || '').replace(/\s+/g, '').trim();
    if (!part) return;

    const matches = part.match(MANTRA_TOKEN_PATTERN);
    if (matches && matches.join('').length === part.length) {
      matches
        .map((t) => normalizeMantraToken(t))
        .filter(Boolean)
        .forEach((token) => {
          if (!unique.includes(token)) unique.push(token);
        });
      return;
    }

    let index = 0;
    while (index < part.length) {
      const two = part.slice(index, index + 2);
      const normalizedTwo = normalizeMantraToken(two);
      if (normalizedTwo) {
        if (!unique.includes(normalizedTwo)) unique.push(normalizedTwo);
        index += 2;
        continue;
      }

      const one = normalizeMantraToken(part.slice(index, index + 1));
      if (one && !unique.includes(one)) unique.push(one);
      index += 1;
    }
  });

  return unique;
}

function getRoleLabel(role) {
  return ROLE_LABELS[role] || role;
}

function getMantraRowLabel(role) {
  const map = {
    P: 'Portiere',
    D: 'Difesa',
    C: 'Centrocampo',
    T: 'Trequarti',
    A: 'Attacco',
  };

  return map[role] || role;
}

function getRoleAccentGroup(role) {
  if (role === 'P') return 'P';
  if (role === 'D') return 'D';
  if (role === 'T') return 'T';
  if (role === 'A') return 'A';
  return 'C';
}

function getRoleSwatchClass(role) {
  return `formation-swatch--${getRoleAccentGroup(role)}`;
}

function getRoleAccentClass(role) {
  return `formation-role-accent--${role}`;
}

function getTokenColorGroup(token) {
  const upper = String(token || '').toUpperCase();
  if (upper === 'P') return 'P';
  if (['DD', 'DC', 'DS', 'B'].includes(upper)) return 'D';
  if (['E', 'M', 'C', 'W', 'T'].includes(upper)) return 'C';
  if (['A', 'PC'].includes(upper)) return 'A';
  return 'C';
}

function valueOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function getPlayerMantraRoles(purchase) {
  const snap = purchase?.playerSnapshot || {};

  // Mirror extractRoleTokens from MantraManageTab: check all known field names,
  // array fields take priority over string fields.
  const candidates = [
    snap.mantraRole, snap.mantraRoles, snap.roleMantra, snap.mantra,
    purchase.mantraRole, purchase.mantraRoles, purchase.roleMantra, purchase.mantra,
    snap.roles, purchase.roles, snap.subRole, purchase.subRole,
    snap.role, purchase.role,
  ];

  const source = candidates.find((c) =>
    Array.isArray(c) ? c.length > 0 : String(c || '').trim() !== ''
  );

  if (!source) return [];

  if (Array.isArray(source)) {
    return [...new Set(source.map((r) => normalizeMantraToken(r)).filter(Boolean))];
  }

  return splitRoleTokens(String(source));
}

function getClassicBroadRole(value) {
  const upper = String(value || '').trim().toUpperCase();

  if (upper === 'P') return 'P';
  if (['D', 'DD', 'DC', 'DS', 'B'].includes(upper)) return 'D';
  if (['C', 'E', 'M', 'W', 'T'].includes(upper)) return 'C';
  if (['A', 'PC'].includes(upper)) return 'A';
  return 'A';
}

function getMantraBroadRole(purchase) {
  const tokens = getPlayerMantraRoles(purchase);
  if (tokens.includes('P')) return 'P';
  if (tokens.some((t) => ['Dd', 'Dc', 'Ds', 'B'].includes(t))) return 'D';
  if (tokens.some((t) => ['E', 'M', 'C'].includes(t))) return 'C';
  if (tokens.some((t) => ['W', 'T'].includes(t))) return 'T';
  return 'A';
}

function getDisplayRoleText(purchase, rolesMode = 'classic') {
  const roleValue = purchase?.playerSnapshot?.role || purchase?.role || 'A';

  if (rolesMode !== 'mantra') {
    return String(roleValue || '').trim().toUpperCase() || 'A';
  }

  const tokens = getPlayerMantraRoles(purchase);
  return tokens.length ? tokens.join('/') : String(roleValue || '—').trim() || '—';
}

function getSlotLabel(slotOptions = []) {
  return slotOptions.join('/');
}

function slotMatchesPlayer(playerTokens, slotOptions) {
  return slotOptions.some((slotRole) => playerTokens.includes(slotRole));
}

function isPlayerInjured(purchase, playerCategoriesById = {}) {
  const key = String(purchase?.id || '');
  const category = String(playerCategoriesById?.[key] || purchase?.category || '').trim().toLowerCase();
  return category === 'injury';
}

export function getBoardStarters(board, rolesMode) {
  if (rolesMode === 'mantra') {
    return (board.rows || []).flatMap((row) => row.map((slot) => slot.player).filter(Boolean));
  }

  return ROLE_ORDER.flatMap((role) => board.startingByRole?.[role] || []);
}

function aggregateStarterStats(starters = [], source = 'current') {
  let markSum = 0;
  let markCount = 0;
  let fmarkSum = 0;
  let fmarkCount = 0;
  let goalsSum = 0;
  let assistsSum = 0;
  let capsSum = 0;
  let capsCount = 0;
  let hasAnyData = false;

  starters.forEach((purchase) => {
    const snap = purchase?.playerSnapshot || {};
    const prev = snap.previousSeasonStats || {};
    const src = source === 'previous' ? prev : snap;

    if (source === 'previous') {
      const prevValues = Object.values(prev || {});
      if (prevValues.some((v) => v !== null && v !== undefined && v !== '')) {
        hasAnyData = true;
      }
    } else {
      hasAnyData = true;
    }

    const markavg = valueOrNull(src.markavg);
    if (markavg !== null) {
      markSum += markavg;
      markCount += 1;
    }

    const fmarkavg = valueOrNull(src.fmarkavg);
    if (fmarkavg !== null) {
      fmarkSum += fmarkavg;
      fmarkCount += 1;
    }

    goalsSum += Number(src.goals || 0);
    assistsSum += Number(src.assists || 0);

    const caps = valueOrNull(src.caps);
    if (caps !== null) {
      capsSum += caps;
      capsCount += 1;
    }
  });

  return {
    playersCount: starters.length,
    avgMark: markCount ? markSum / markCount : null,
    avgFMark: fmarkCount ? fmarkSum / fmarkCount : null,
    totalGoals: starters.length ? goalsSum : null,
    totalAssists: starters.length ? assistsSum : null,
    avgCaps: capsCount ? capsSum / capsCount : null,
    hasAnyData,
  };
}

export function buildClassicBoard(roster = [], formationKey = '4-3-3', adjustments = null) {
  const presetKey = normalizeFormationKey(formationKey, 'classic');
  const preset = CLASSIC_PRESETS[presetKey] || CLASSIC_PRESETS['4-3-3'];

  const grouped = { P: [], D: [], C: [], A: [] };

  roster.forEach((purchase) => {
    const roleValue = purchase?.playerSnapshot?.role || purchase?.role || 'A';
    const broadRole = getClassicBroadRole(roleValue);

    if (!grouped[broadRole]) grouped[broadRole] = [];

    const baseScore = Number(calcPlayerScore(purchase) || 0);
    const { score, formDelta, availabilityFlag } = applyMatchdayAdjustments(baseScore, purchase, adjustments || {});

    grouped[broadRole].push({
      ...purchase,
      score,
      availabilityFlag,
      formDelta,
    });
  });

  ROLE_ORDER.forEach((role) => {
    grouped[role].sort((a, b) => {
      const diff = (Number(b.score) || 0) - (Number(a.score) || 0);
      if (diff !== 0) return diff;
      return (a.playerSnapshot?.name || a.playerName || '').localeCompare(
        b.playerSnapshot?.name || b.playerName || ''
      );
    });
  });

  const startingByRole = {};
  const benchByRole = {};

  ROLE_ORDER.forEach((role) => {
    const needed = preset[role] || 0;
    const sorted = grouped[role] || [];
    startingByRole[role] = sorted.slice(0, needed);
    benchByRole[role] = sorted.slice(needed);
  });

  const missingByRole = ROLE_ORDER.reduce((acc, role) => {
    const needed = preset[role] || 0;
    const have = (startingByRole[role] || []).length;
    acc[role] = Math.max(needed - have, 0);
    return acc;
  }, {});

  return {
    mode: 'classic',
    formationKey: presetKey,
    preset,
    startingByRole,
    benchByRole,
    missingByRole,
    complete: Object.values(missingByRole).every((n) => n === 0),
    totalNeeded: Object.values(preset).reduce((sum, n) => sum + n, 0),
    totalStarters: ROLE_ORDER.reduce((sum, role) => sum + (startingByRole[role] || []).length, 0),
    totalBench: ROLE_ORDER.reduce((sum, role) => sum + (benchByRole[role] || []).length, 0),
  };
}

export function buildMantraBoard(roster = [], formationKey = '343', adjustments = null) {
  const presetKey = normalizeFormationKey(formationKey, 'mantra');
  const preset = MANTRA_PRESETS[presetKey] || MANTRA_PRESETS['343'];

  const players = roster
    .map((purchase) => {
      const baseScore = Number(calcPlayerScore(purchase) || 0);
      const { score, formDelta, availabilityFlag } = applyMatchdayAdjustments(baseScore, purchase, adjustments || {});
      return {
        ...purchase,
        score,
        formDelta,
        availabilityFlag,
        roleTokens: getPlayerMantraRoles(purchase),
      };
    })
    .sort((a, b) => {
      const diff = (Number(b.score) || 0) - (Number(a.score) || 0);
      if (diff !== 0) return diff;
      return (a.playerSnapshot?.name || a.playerName || '').localeCompare(
        b.playerSnapshot?.name || b.playerName || ''
      );
    });

  const slots = [];
  preset.rows.forEach((row, rowIndex) => {
    row.slots.forEach((options, slotIndex) => {
      slots.push({
        rowIndex,
        slotIndex,
        rowKey: row.key,
        rowTitle: row.title,
        rowSubtitle: row.subtitle,
        slotLabel: getSlotLabel(options),
        options,
      });
    });
  });

  const compatibleCountByPlayerId = new Map();
  players.forEach((player) => {
    const count = slots.reduce(
      (acc, slot) => acc + (slotMatchesPlayer(player.roleTokens, slot.options) ? 1 : 0),
      0
    );
    compatibleCountByPlayerId.set(player.id, count);
  });

  const slotCandidates = slots.map((slot) => {
    const candidates = players
      .filter((player) => slotMatchesPlayer(player.roleTokens, slot.options))
      .map((player) => {
        const compatibleCount = Math.max(compatibleCountByPlayerId.get(player.id) || 1, 1);
        const slotFlex = Math.max(slot.options.length, 1);
        const exactSingleRoleBonus =
          slot.options.length === 1 && player.roleTokens.includes(slot.options[0]) ? 3.5 : 0;
        const allInOneBonus = player.roleTokens.length === 1 ? 1.2 : 0;

        const fitScore =
          Number(player.score || 0) +
          18 / compatibleCount +
          7 / slotFlex +
          exactSingleRoleBonus +
          allInOneBonus;

        return {
          ...player,
          fitScore,
        };
      })
      .sort((a, b) => {
        const diff = (Number(b.fitScore) || 0) - (Number(a.fitScore) || 0);
        if (diff !== 0) return diff;
        const scoreDiff = (Number(b.score) || 0) - (Number(a.score) || 0);
        if (scoreDiff !== 0) return scoreDiff;
        return (a.playerSnapshot?.name || a.playerName || '').localeCompare(
          b.playerSnapshot?.name || b.playerName || ''
        );
      })
      .slice(0, 12);

    return candidates;
  });

  const slotOrder = slots
    .map((slot, index) => ({
      index,
      count: slotCandidates[index].length,
      flex: slot.options.length,
      rowPriority: MANTRA_ROW_ORDER.indexOf(slot.rowKey),
    }))
    .sort((a, b) => {
      if (a.count !== b.count) return a.count - b.count;
      if (a.flex !== b.flex) return a.flex - b.flex;
      if (a.rowPriority !== b.rowPriority) return a.rowPriority - b.rowPriority;
      return a.index - b.index;
    });

  const maxRemainingScoreByPosition = Array(slotOrder.length + 1).fill(0);
  for (let i = slotOrder.length - 1; i >= 0; i -= 1) {
    const slotIndex = slotOrder[i].index;
    const best = slotCandidates[slotIndex][0]?.fitScore || 0;
    maxRemainingScoreByPosition[i] = maxRemainingScoreByPosition[i + 1] + best;
  }

  const assignment = Array(slots.length).fill(null);
  let bestAssignment = assignment.slice();
  let bestScore = -Infinity;
  const used = new Set();

  function dfs(position, currentScore) {
    if (position >= slotOrder.length) {
      if (currentScore > bestScore) {
        bestScore = currentScore;
        bestAssignment = assignment.slice();
      }
      return;
    }

    const upperBound = currentScore + maxRemainingScoreByPosition[position];
    if (upperBound <= bestScore) return;

    const slotIndex = slotOrder[position].index;
    const candidates = slotCandidates[slotIndex];
    let placedAny = false;

    for (const candidate of candidates) {
      if (used.has(candidate.id)) continue;

      placedAny = true;
      used.add(candidate.id);
      assignment[slotIndex] = candidate;

      dfs(position + 1, currentScore + candidate.fitScore);

      assignment[slotIndex] = null;
      used.delete(candidate.id);
    }

    if (!placedAny) {
      dfs(position + 1, currentScore);
    }
  }

  dfs(0, 0);

  const rows = preset.rows.map((row) =>
    row.slots.map((options, slotIndex) => ({
      rowKey: row.key,
      rowTitle: row.title,
      rowSubtitle: row.subtitle,
      slotIndex,
      options,
      slotLabel: getSlotLabel(options),
      player: null,
    }))
  );

  slots.forEach((slot, index) => {
    rows[slot.rowIndex][slot.slotIndex].player = bestAssignment[index] || null;
  });

  const starters = bestAssignment.filter(Boolean);
  const bench = players.filter((player) => !starters.some((starter) => starter.id === player.id));

  const missingByRole = { P: 0, D: 0, C: 0, T: 0, A: 0 };
  rows.forEach((row) => {
    const rowKey = row[0]?.rowKey;
    const missing = row.filter((slot) => !slot.player).length;
    if (rowKey && missingByRole[rowKey] !== undefined) {
      missingByRole[rowKey] += missing;
    }
  });

  return {
    mode: 'mantra',
    formationKey: presetKey,
    preset,
    rows,
    bench,
    complete: starters.length === slots.length,
    totalNeeded: slots.length,
    totalStarters: starters.length,
    totalBench: bench.length,
    missingByRole,
  };
}

function buildMantraBenchGroups(bench = [], rowKeys = MANTRA_ROW_ORDER) {
  const grouped = {};
  rowKeys.forEach((key) => {
    grouped[key] = [];
  });

  bench.forEach((purchase) => {
    const roleKey = getMantraBroadRole(purchase);
    let target = roleKey;

    if (!grouped[target]) {
      if (roleKey === 'T' && grouped.C) target = 'C';
      else if (roleKey === 'T' && grouped.A) target = 'A';
      else if ((roleKey === 'C' || roleKey === 'D') && grouped.C) target = 'C';
      else if (grouped.A) target = 'A';
      else target = rowKeys[0] || 'A';
    }

    if (!grouped[target]) grouped[target] = [];
    grouped[target].push(purchase);
  });

  return grouped;
}

function swapPlayersWithinClassicBoard(prev, role, starterId, benchId) {
  const starters = [...(prev.startingByRole?.[role] || [])];
  const bench = [...(prev.benchByRole?.[role] || [])];

  const starterIndex = starters.findIndex((p) => String(p.id) === String(starterId));
  const benchIndex = bench.findIndex((p) => String(p.id) === String(benchId));

  if (starterIndex === -1 || benchIndex === -1) return prev;

  [starters[starterIndex], bench[benchIndex]] = [bench[benchIndex], starters[starterIndex]];

  const nextStartingByRole = {
    ...prev.startingByRole,
    [role]: starters,
  };

  const nextBenchByRole = {
    ...prev.benchByRole,
    [role]: bench,
  };

  return {
    ...prev,
    startingByRole: nextStartingByRole,
    benchByRole: nextBenchByRole,
    totalStarters: ROLE_ORDER.reduce((sum, r) => sum + (nextStartingByRole[r] || []).length, 0),
    totalBench: ROLE_ORDER.reduce((sum, r) => sum + (nextBenchByRole[r] || []).length, 0),
  };
}

function swapPlayersWithinMantraBoard(prev, starterId, benchId) {
  const rows = prev.rows.map((row) =>
    row.map((slot) => ({
      ...slot,
      player: slot.player ? { ...slot.player } : null,
    }))
  );
  const bench = [...(prev.bench || [])];

  let starterSlot = null;
  rows.forEach((row) => {
    row.forEach((slot) => {
      if (slot.player && String(slot.player.id) === String(starterId)) {
        starterSlot = slot;
      }
    });
  });

  if (!starterSlot) return prev;

  const benchIndex = bench.findIndex((p) => String(p.id) === String(benchId));
  if (benchIndex === -1) return prev;

  const benchPlayer = bench[benchIndex];
  if (!slotMatchesPlayer(benchPlayer.roleTokens || getPlayerMantraRoles(benchPlayer), starterSlot.options)) {
    return prev;
  }

  const starterPlayer = starterSlot.player;
  starterSlot.player = benchPlayer;
  bench[benchIndex] = starterPlayer;

  const starters = rows.flatMap((row) => row.map((slot) => slot.player).filter(Boolean));

  return {
    ...prev,
    rows,
    bench,
    complete: starters.length === rows.reduce((sum, row) => sum + row.length, 0),
    totalStarters: starters.length,
    totalBench: bench.length,
  };
}

function FormationStatCard({ label, value, sublabel = '' }) {
  return (
    <div className="formation-stat-card">
      <span>{label}</span>
      <strong>{value}</strong>
      {sublabel ? <em>{sublabel}</em> : null}
    </div>
  );
}

export function RolePills({ purchase, rolesMode, showRoles = true }) {
  const roleText = getDisplayRoleText(purchase, rolesMode);

  if (!showRoles) return null;

  if (rolesMode !== 'mantra') {
    const broadRole = getClassicBroadRole(purchase?.playerSnapshot?.role || purchase?.role || 'A');

    return (
      <div className="formation-role-badges" aria-label={roleText}>
        <span className={`formation-role-pill formation-role-pill--classic formation-role-pill--classic-${broadRole}`}>
          {broadRole}
        </span>
      </div>
    );
  }

  const tokens = getPlayerMantraRoles(purchase);

  return (
    <div className="formation-role-badges" aria-label={roleText}>
      {tokens.length ? (
        tokens.map((token) => {
          const group = getTokenColorGroup(token);
          return (
            <span
              key={`${purchase?.id || 'player'}-${token}`}
              className={`formation-role-pill formation-role-pill--mantra formation-role-pill--mantra-${group}`}
            >
              {token}
            </span>
          );
        })
      ) : (
        <span className="formation-role-pill formation-role-pill--mantra formation-role-pill--mantra-A">—</span>
      )}
    </div>
  );
}

function FormationPlayerCard({
  purchase,
  variant,
  selected,
  swapped,
  injured,
  playProbability,
  onClick,
  rolesMode,
  showRoles = true,
  clickable = true,
  showFixtures = false,
  title,
}) {
  const snap = purchase?.playerSnapshot || {};
  const team = snap.team || purchase?.playerTeam || '—';
  const name = snap.name || purchase?.playerName || '—';

  return (
    <AppTooltip
      as="button"
      type="button"
      className={[
        'formation-player-card',
        `formation-player-card--${variant}`,
        showFixtures ? 'formation-player-card--withFixtures' : '',
        playProbability && !showFixtures ? 'formation-player-card--hasProb' : '',
        selected ? 'is-selected' : '',
        swapped ? 'is-swapped' : '',
        clickable ? '' : 'is-static',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={clickable ? onClick : undefined}
      disabled={!clickable}
      content={title || (clickable ? 'Clicca per sostituire' : undefined)}
    >
      <TeamBadge team={team} size={30} />
      <div className="formation-player-card__body">
        <span className="formation-player-card__nameRow">
          <strong className="formation-player-card__name">{name}</strong>
          {injured ? (
            <AppTooltip as="span" className="formation-injury-badge" content="Infortunato" aria-label="Infortunato">
              <Bandage size={12} />
            </AppTooltip>
          ) : null}
          {typeof purchase?.formDelta === 'number' && Math.abs(purchase.formDelta) > 1 ? (
            <AppTooltip
              as="span"
              className={`formation-form-badge formation-form-badge--${purchase.formDelta > 0 ? 'up' : 'down'}`}
              content={
                purchase.formDelta > 0
                  ? `In un buon momento: nelle ultime giornate fa ${purchase.formDelta.toFixed(2)} in più della sua media stagionale.`
                  : `In calo: nelle ultime giornate fa ${Math.abs(purchase.formDelta).toFixed(2)} in meno della sua media stagionale.`
              }
              aria-label="Forma recente"
            >
              {purchase.formDelta > 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            </AppTooltip>
          ) : null}
        </span>
        <span className="formation-player-card__roleRow">
          <RolePills purchase={purchase} rolesMode={rolesMode} showRoles={showRoles} />
          {playProbability && showFixtures ? (
            <AppTooltip
              as="span"
              className={`formation-playprob-badge formation-playprob-badge--${playProbability.status}`}
              content={getPlayProbabilityLabel(playProbability)}
            >
              {Number(playProbability.prob)}%
            </AppTooltip>
          ) : null}
        </span>
      </div>
      {playProbability && !showFixtures ? (
        <AppTooltip
          as="span"
          className={`formation-playprob-badge formation-playprob-badge--corner formation-playprob-badge--${playProbability.status}`}
          content={getPlayProbabilityLabel(playProbability)}
        >
          {Number(playProbability.prob)}%
        </AppTooltip>
      ) : null}
      {showFixtures ? (
        <div className="formation-player-card__fixtures">
          <TeamNextFixtures team={team} count={3} compact />
        </div>
      ) : null}
    </AppTooltip>
  );
}

function EmptySlotCard({ label = 'Slot mancante' }) {
  return (
    <div className="formation-player-card formation-player-card--empty">
      <span className="formation-empty-dot" />
      <div className="formation-player-card__body">
        <strong className="formation-player-card__name">{label}</strong>
      </div>
    </div>
  );
}

function PitchRow({
  row,
  players,
  required,
  missing,
  onStarterClick,
  rolesMode,
  slots,
  playerCategoriesById,
  playProbabilityById = {},
  swappedIds,
  openStarterId,
}) {
  return (
    <section className={`formation-pitch__row ${getRoleAccentClass(row.key)}`}>
      <div className={`formation-pitch__rowHead ${getRoleAccentClass(row.key)}`}>
        <div className="formation-pitch__rowTitle">
          <span className={`formation-pitch__swatch ${getRoleSwatchClass(row.key)}`} />
          <div className="formation-pitch__rowTitleText">
            <strong>{row.title}</strong>
            <span>{row.subtitle}</span>
          </div>
        </div>

        <div className="formation-pitch__rowCount">
          <strong>{players.length}</strong>
          <span>/{required}</span>
        </div>
      </div>

      <div className="formation-pitch__rowCards">
        {rolesMode === 'mantra' && slots
          ? slots.map((slot, i) =>
              slot.player
                ? (
                  <FormationPlayerCard
                    key={slot.player.id}
                    purchase={slot.player}
                    variant="starter"
                    rolesMode={rolesMode}
                    showRoles
                    selected={openStarterId === slot.player.id}
                    swapped={swappedIds?.has(slot.player.id)}
                    injured={isPlayerInjured(slot.player, playerCategoriesById)}
                    playProbability={playProbabilityById?.[String(slot.player.id)]}
                    onClick={() =>
                      onStarterClick({ role: row.key, id: slot.player.id, options: slot.options })
                    }
                  />
                )
                : <EmptySlotCard key={`${row.key}-missing-${i}`} label={slot.slotLabel} />
            )
          : (
            <>
              {players.map((purchase) => (
                <FormationPlayerCard
                  key={purchase.id}
                  purchase={purchase}
                  variant="starter"
                  rolesMode={rolesMode}
                  showRoles
                  selected={openStarterId === purchase.id}
                  swapped={swappedIds?.has(purchase.id)}
                  injured={isPlayerInjured(purchase, playerCategoriesById)}
                  playProbability={playProbabilityById?.[String(purchase.id)]}
                  onClick={() =>
                    onStarterClick({ role: row.key, id: purchase.id })
                  }
                />
              ))}
              {Array.from({ length: missing }).map((_, index) => (
                <EmptySlotCard key={`${row.key}-missing-${index}`} label={row.slotLabels?.[index] || 'Slot mancante'} />
              ))}
            </>
          )
        }
      </div>
    </section>
  );
}

function SwapOverlay({ starter, candidates, rolesMode, playerCategoriesById, playProbabilityById = {}, onPick, onClose }) {
  if (!starter || typeof document === 'undefined') return null;

  const snap = starter.playerSnapshot || {};
  const name = snap.name || starter.playerName || '—';

  return createPortal(
    <div className="formation-swap-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="formation-swap-overlay__panel" onClick={(e) => e.stopPropagation()}>
        <div className="formation-swap-overlay__head">
          <div>
            <span className="formation-swap-overlay__eyebrow">Sostituisci</span>
            <strong>{name}</strong>
          </div>
          <button type="button" className="formation-swap-overlay__close" onClick={onClose} aria-label="Chiudi">
            ✕
          </button>
        </div>

        <div className="formation-swap-overlay__list">
          {candidates.length ? (
            candidates.map((purchase) => (
              <FormationPlayerCard
                key={purchase.id}
                purchase={purchase}
                variant="bench"
                rolesMode={rolesMode}
                showRoles
                showFixtures
                injured={isPlayerInjured(purchase, playerCategoriesById)}
                playProbability={playProbabilityById?.[String(purchase.id)]}
                onClick={() => onPick(purchase.id)}
              />
            ))
          ) : (
            <div className="formation-bench-group__empty">Nessuna riserva compatibile in panchina.</div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Overlay "Probabili titolari di Serie A" ─────────────────────────────────
// Grazie alle probabili formazioni (playProbabilityById), per ogni squadra di
// Serie A mostra i titolari previsti; quelli già acquistati da una squadra
// dell'asta sono evidenziati (colore diverso + nome squadra proprietaria).
const PROB_ROLE_COLOR = { P: '#f5c542', D: '#22c55e', C: '#3b82f6', A: '#ef4444' };

function ProbRoleCell({ tokens, bucket, rolesMode }) {
  if (rolesMode === 'mantra' && tokens && tokens.length) {
    return (
      <span className="formation-prob-row__mtokens">
        {tokens.map((t) => {
          const th = MANTRA_ROLE_THEME[t] || MANTRA_ROLE_THEME.A;
          return (
            <span
              key={t}
              className="formation-prob-mtoken"
              style={{ background: th.bg, border: `1px solid ${th.border}`, color: th.color }}
            >
              {t}
            </span>
          );
        })}
      </span>
    );
  }
  return (
    <span className="formation-prob-row__role" style={{ color: PROB_ROLE_COLOR[bucket] || PROB_ROLE_COLOR.A }}>
      {bucket}
    </span>
  );
}

function ProbabiliOverlay({ teams, boughtByPlayerId, rolesMode = 'classic', onClose }) {
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div className="formation-prob-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="formation-prob-overlay__panel" onClick={(e) => e.stopPropagation()}>
        <div className="formation-swap-overlay__head">
          <div>
            <span className="formation-swap-overlay__eyebrow">Probabili formazioni</span>
            <strong>Titolari per squadra di Serie A</strong>
          </div>
          <button type="button" className="formation-swap-overlay__close" onClick={onClose} aria-label="Chiudi">✕</button>
        </div>

        {teams.length ? (
          <div className="formation-prob-overlay__grid">
            {teams.map(({ team, players }) => {
              const taken = players.filter((p) => boughtByPlayerId.has(String(p.id))).length;
              return (
                <div key={team} className="formation-prob-team">
                  <div className="formation-prob-team__head">
                    <TeamBadge team={team} size={22} showTooltip={false} />
                    <strong>{team}</strong>
                    <span>{taken}/{players.length} presi</span>
                  </div>
                  <ul className="formation-prob-team__list">
                    {players.map((p) => {
                      const owner = boughtByPlayerId.get(String(p.id));
                      return (
                        <li key={p.id} className={`formation-prob-row ${owner ? 'is-taken' : ''}`}>
                          <ProbRoleCell tokens={p.tokens} bucket={p.bucket} rolesMode={rolesMode} />
                          <span className="formation-prob-row__name">{p.name}</span>
                          {p.prob > 0 && <span className="formation-prob-row__pct">{p.prob}%</span>}
                          {owner && (
                            <span className="formation-prob-row__owner" title={`Preso da ${owner}`}>{owner}</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="formation-prob-overlay__empty">
            Le probabili formazioni non sono ancora disponibili. Riprova tra poco.
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// ─── Sostituzioni & ordine panchina (solo Mantra) ────────────────────────────
// Riusa l'undici REALE già scelto sul campetto (board.rows/board.bench),
// non ne ricalcola uno nuovo: la simulazione parte da lì.

function flattenMantraBoardSlots(board) {
  const slots = [];
  const assignment = [];

  (board.rows || []).forEach((row) => {
    row.forEach((slot) => {
      slots.push({ rowKey: slot.rowKey, slotIndex: slot.slotIndex, options: slot.options, label: slot.slotLabel });
      assignment.push(slot.player || null);
    });
  });

  return { slots, assignment };
}

function getPurchaseRoles(purchase) {
  return purchase?.roleTokens || getPlayerMantraRoles(purchase);
}

function getPurchaseName(purchase) {
  return purchase?.playerSnapshot?.name || purchase?.playerName || '—';
}

function SubstitutionResultCard({ simulation, absentPlayer, mode }) {
  const absentName = getPurchaseName(absentPlayer);

  if (!simulation?.resolved) {
    return (
      <div className="formation-subs__alert">
        <strong>Nessuna soluzione disponibile.</strong> In modalità {MODE_LABELS[mode]} nessun panchinaro
        può coprire lo slot di {absentName} ({simulation?.vacantSlot?.label}).
        {mode === 'easy' && ' Prova Base o Master: ammettono anche il cambio modulo.'}
      </div>
    );
  }

  const { candidate, malus, malusCount = 0, malusFlags, moduleChanged, newPresetKey, isGoalkeeperSlot, resultSlots, resultAssignment } = simulation;
  const lineupRows = groupResultByRow(resultSlots, resultAssignment, malusFlags);
  const otherMalusCount = Math.max(0, malusCount - (malus ? 1 : 0));

  return (
    <div className="formation-subs__result">
      <div className="formation-subs__resultRow">
        <span className="formation-subs__resultOut">{absentName}</span>
        <span className="formation-subs__resultArrow">→</span>
        <span className="formation-subs__resultIn">{getPurchaseName(candidate)}</span>
      </div>
      <div className="formation-subs__resultBadges">
        {malusCount > 0
          ? <span className="formation-subs__badge formation-subs__badge--warn">Malus −{malusCount}</span>
          : <span className="formation-subs__badge formation-subs__badge--pos">Nessun malus</span>}
        {moduleChanged
          ? <span className="formation-subs__badge formation-subs__badge--info">Cambio modulo → {MANTRA_PRESETS[newPresetKey]?.label}</span>
          : <span className="formation-subs__badge formation-subs__badge--ok">Modulo invariato</span>}
        {isGoalkeeperSlot && <span className="formation-subs__badge formation-subs__badge--warn">Portiere: priorità assoluta</span>}
      </div>
      {otherMalusCount > 0 && (
        <div className="formation-subs__resultNote">
          La riallocazione dell'undici sposta fuori ruolo anche {otherMalusCount === 1 ? 'un altro titolare' : `altri ${otherMalusCount} titolari`} (evidenziati sotto).
        </div>
      )}

      <div className="formation-subs__resultLineup">
        <span className="formation-subs__resultLineupLabel">
          Nuovo modulo — {MANTRA_PRESETS[newPresetKey]?.label || newPresetKey}
        </span>
        <div className="formation-subs__resultLineupRows">
          {lineupRows.map(({ key, players }) => (
            <div key={key} className="formation-subs__resultLineupRow">
              <span className="formation-subs__resultLineupRowLabel">{getMantraRowLabel(key)}</span>
              <span className="formation-subs__resultLineupRowNames">
                {players.map(({ player: p, malus: pMalus }, i) => (
                  <React.Fragment key={p?.id ?? i}>
                    {i > 0 && <span className="formation-subs__resultLineupSep"> · </span>}
                    <AppTooltip
                      as="span"
                      className={[
                        p && candidate && p.id === candidate.id ? 'is-new' : '',
                        pMalus ? 'is-malus' : '',
                      ].filter(Boolean).join(' ')}
                      content={pMalus ? 'Schierato fuori ruolo (malus −1)' : undefined}
                    >
                      {p ? getPurchaseName(p) : '—'}
                    </AppTooltip>
                  </React.Fragment>
                ))}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function groupResultByRow(resultSlots = [], resultAssignment = [], malusFlags = []) {
  const groups = {};
  resultSlots.forEach((slot, i) => {
    if (!groups[slot.rowKey]) groups[slot.rowKey] = [];
    groups[slot.rowKey].push({ player: resultAssignment[i], malus: Boolean(malusFlags[i]) });
  });
  return MANTRA_ROW_ORDER.filter((key) => groups[key]).map((key) => ({ key, players: groups[key] }));
}

function MantraSubstitutionPanel({ board }) {
  const presetKey = board.formationKey;
  const { slots, assignment } = useMemo(() => flattenMantraBoardSlots(board), [board]);

  const [order, setOrder] = useState(() =>
    (board.bench || []).slice().sort((a, b) => (b.score || 0) - (a.score || 0)).map((p) => p.id)
  );

  useEffect(() => {
    setOrder((prevOrder) => {
      const benchIds = new Set((board.bench || []).map((p) => p.id));
      const stillValid = prevOrder.filter((id) => benchIds.has(id));
      const missing = (board.bench || [])
        .filter((p) => !stillValid.includes(p.id))
        .sort((a, b) => (b.score || 0) - (a.score || 0))
        .map((p) => p.id);
      const next = [...stillValid, ...missing];
      return next.length === prevOrder.length && next.every((id, i) => id === prevOrder[i]) ? prevOrder : next;
    });
  }, [board.bench]);

  const benchById = useMemo(() => {
    const map = {};
    (board.bench || []).forEach((p) => { map[p.id] = p; });
    return map;
  }, [board.bench]);

  const orderedBench = useMemo(() => order.map((id) => benchById[id]).filter(Boolean), [order, benchById]);

  const moveBenchPlayer = (index, direction) => {
    setOrder((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const reorderBenchPlayer = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    setOrder((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const [dragIndex, setDragIndex] = useState(null);

  const [absentId, setAbsentId] = useState(null);
  const [mode, setMode] = useState('basic');
  const [recalcTick, setRecalcTick] = useState(0);

  useEffect(() => { setAbsentId(null); }, [presetKey]);

  const absentPlayer = assignment.find((p) => p && p.id === absentId) || null;

  const simulation = useMemo(() => {
    if (!absentId) return null;
    return simulateAbsence({
      presetKey,
      slots,
      assignment,
      benchOrder: orderedBench,
      absentId,
      mode,
      rolesOf: getPurchaseRoles,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [absentId, mode, presetKey, slots, assignment, orderedBench, recalcTick]);

  return (
    <section className="formation-subs">
      <div className="formation-subs__head">
        <div>
          <h3>Sostituzioni & ordine panchina Mantra</h3>
          <span>Simula chi entra se un titolare del tuo undici è assente, secondo le 3 modalità ufficiali del sistema Mantra.</span>
        </div>
      </div>

      <div className="formation-subs__grid">
        <div className="formation-subs__main">
          <div className="formation-subs__step">
            <span className="formation-subs__stepNum">1</span>
            <div className="formation-subs__stepBody">
              <strong>Chi è assente?</strong>
              <div className="formation-subs__starters">
                {slots.map((slot, i) => {
                  const p = assignment[i];
                  const isAbsent = p && absentId === p.id;
                  return (
                    <AppTooltip
                      as="button"
                      key={`${slot.rowKey}-${slot.slotIndex}`}
                      type="button"
                      className={`formation-subs__starterChip ${isAbsent ? 'is-absent' : ''} ${!p ? 'is-empty' : ''}`}
                      disabled={!p}
                      onClick={() => p && setAbsentId(isAbsent ? null : p.id)}
                      content={p ? `Segna ${getPurchaseName(p)} come assente` : 'Slot scoperto'}
                    >
                      {p && <RolePills purchase={p} rolesMode="mantra" showRoles />}
                      <span>{p ? getPurchaseName(p) : slot.label}</span>
                    </AppTooltip>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="formation-subs__step">
            <span className="formation-subs__stepNum">2</span>
            <div className="formation-subs__stepBody">
              <strong>Modalità di sostituzione</strong>
              <div className="formation-subs__modes">
                {['basic', 'easy', 'master'].map((m) => (
                  <button
                    key={m}
                    type="button"
                    className={`formation-subs__modeCard ${mode === m ? 'is-active' : ''}`}
                    onClick={() => setMode(m)}
                  >
                    <strong>{MODE_LABELS[m]}</strong>
                    <span>{MODE_DESCRIPTIONS[m]}</span>
                  </button>
                ))}
              </div>

              <AppTooltip
                as="button"
                type="button"
                className="formation-subs__recalcBtn"
                disabled={!absentPlayer}
                onClick={() => setRecalcTick((t) => t + 1)}
                content="Ricalcola la simulazione con l'ordine panchina e la modalità attuali"
              >
                <RefreshCw size={13} />
                Ricalcola
              </AppTooltip>

              {absentPlayer ? (
                <SubstitutionResultCard simulation={simulation} absentPlayer={absentPlayer} mode={mode} />
              ) : (
                <div className="formation-subs__resultEmpty">Segna un titolare come assente qui sopra per vedere chi entra.</div>
              )}
            </div>
          </div>
        </div>

        <div className="formation-subs__bench">
          <strong>Ordine di preferenza panchina</strong>
          <span className="formation-subs__benchHint">Chi è più in alto entra per primo in caso di sostituzione, a prescindere dal ruolo. Trascina per riordinare, o usa le frecce.</span>
          <div className="formation-subs__benchList">
            {orderedBench.length === 0 && <div className="formation-bench-group__empty">Nessuna riserva disponibile.</div>}
            {orderedBench.map((p, index) => (
              <div
                key={p.id}
                className={`formation-subs__benchItem ${dragIndex === index ? 'is-dragging' : ''}`}
                draggable
                onDragStart={(e) => { setDragIndex(index); e.dataTransfer.effectAllowed = 'move'; }}
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (dragIndex !== null) reorderBenchPlayer(dragIndex, index);
                  setDragIndex(null);
                }}
                onDragEnd={() => setDragIndex(null)}
              >
                <AppTooltip as="span" className="formation-subs__benchHandle" aria-hidden="true" content="Trascina per riordinare">⠿</AppTooltip>
                <span className="formation-subs__benchRank">{index + 1}</span>
                <TeamBadge team={p.playerSnapshot?.team || p.playerTeam || '—'} size={20} />
                <span className="formation-subs__benchName">{getPurchaseName(p)}</span>
                <RolePills purchase={p} rolesMode="mantra" showRoles />
                <div className="formation-subs__benchActions">
                  <button type="button" disabled={index === 0} onClick={() => moveBenchPlayer(index, -1)} aria-label="Sposta su">▲</button>
                  <button type="button" disabled={index === orderedBench.length - 1} onClick={() => moveBenchPlayer(index, 1)} aria-label="Sposta giù">▼</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Distribuzione squadre Serie A (classic + mantra) ────────────────────────
// Quanti giocatori in rosa per ciascun club: aiuta a non sbilanciarsi sul
// calendario di una singola squadra. Colore stabile per squadra via hash del
// nome (non c'è una palette validata per ~20 squadre reali), ma l'identità
// primaria in legenda/tooltip resta lo stemma (TeamBadge), non il colore.

function getTeamColor(team = '', isDark = false) {
  let hash = 0;
  const str = String(team);
  for (let i = 0; i < str.length; i += 1) hash = (hash * 31 + str.charCodeAt(i)) | 0;
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 62%, ${isDark ? 58 : 44}%)`;
}

// Oltre questa soglia la rosa dipende troppo dal calendario di una singola
// squadra (favorevole o sfavorevole che sia): evidenziato in legenda.
const TEAM_CONCENTRATION_THRESHOLD = 3;

function buildTeamDistribution(purchases) {
  const counts = new Map();
  (purchases || []).forEach((purchase) => {
    const team = purchase?.playerSnapshot?.team || purchase?.playerTeam || 'Sconosciuta';
    counts.set(team, (counts.get(team) || 0) + 1);
  });
  return [...counts.entries()]
    .map(([team, count]) => ({ team, count }))
    .sort((a, b) => b.count - a.count || a.team.localeCompare(b.team));
}

function TeamDistributionTooltip({ active, payload, isDark, total }) {
  if (!active || !payload?.length) return null;
  const { team, count } = payload[0].payload;
  const pct = total ? Math.round((count / total) * 100) : 0;
  return (
    <div className="formation-teamdist__tooltip" style={{ background: isDark ? '#0f172a' : '#ffffff', color: isDark ? '#f8fafc' : '#111827' }}>
      <strong className="formation-teamdist__tooltipLabel">{team}</strong>
      <div>{count} giocatore{count === 1 ? '' : 'i'} · {pct}%</div>
    </div>
  );
}

function TeamDistributionSection({ purchases }) {
  const { isDark } = useTheme();
  const data = useMemo(() => buildTeamDistribution(purchases), [purchases]);
  const total = data.reduce((acc, d) => acc + d.count, 0);

  if (!data.length) return null;

  return (
    <section className="formation-teamdist">
      <div className="formation-teamdist__head">
        <div>
          <h3>Distribuzione per Squadra Serie A</h3>
          <span>Quanti giocatori in rosa per ciascun club — utile per non sbilanciarti su un solo calendario.</span>
        </div>
        <div className="formation-teamdist__badge">
          <PieChartIcon size={14} />
          <span>{data.length} squadre rappresentate</span>
        </div>
      </div>

      <div className="formation-teamdist__grid">
        <div className="formation-teamdist__chart">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={data}
                dataKey="count"
                nameKey="team"
                innerRadius={64}
                outerRadius={104}
                paddingAngle={2}
                cornerRadius={3}
                stroke="none"
              >
                {data.map((entry) => (
                  <Cell key={entry.team} fill={getTeamColor(entry.team, isDark)} />
                ))}
              </Pie>
              <Tooltip content={<TeamDistributionTooltip isDark={isDark} total={total} />} />
            </PieChart>
          </ResponsiveContainer>
          <div className="formation-teamdist__center">
            <strong>{total}</strong>
            <span>giocatori</span>
          </div>
        </div>

        <div className="formation-teamdist__legend">
          {data.map(({ team, count }) => {
            const isHeavy = count > TEAM_CONCENTRATION_THRESHOLD;
            return (
              <div key={team} className={`formation-teamdist__row${isHeavy ? ' formation-teamdist__row--heavy' : ''}`}>
                <span className="formation-teamdist__dot" style={{ background: getTeamColor(team, isDark) }} />
                <TeamBadge team={team} size={22} showTooltip={false} />
                <AppTooltip as="span" className="formation-teamdist__name" content={team}>{team}</AppTooltip>
                {isHeavy && (
                  <AppTooltip
                    as="span"
                    className="formation-teamdist__heavyIcon"
                    content={`Più di ${TEAM_CONCENTRATION_THRESHOLD} giocatori dalla stessa squadra: rosa esposta al calendario del ${team}.`}
                  >
                    <AlertTriangle size={13} />
                  </AppTooltip>
                )}
                <span className="formation-teamdist__count">{count}</span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default function AuctionFormationTab({
  auction,
  rolesMode = 'classic',
  rosterByTeam = {},
  teamNames = [],
  players = [],
  selectedTeamName = '',
  onSelectedTeamNameChange,
  myRosterPurchases,
  initialFormation = '4-3-3',
  saving = false,
  onSavePlayerValuation,
  playerCategoriesById = {},
  playProbabilityById = {},
}) {
  const normalizedInitial = normalizeFormationKey(initialFormation, rolesMode);

  const activeRosterPurchases = useMemo(() => {
    if (selectedTeamName && rosterByTeam?.[selectedTeamName]) {
      return rosterByTeam[selectedTeamName];
    }

    return myRosterPurchases || [];
  }, [rosterByTeam, selectedTeamName, myRosterPurchases]);

  const [selectedFormation, setSelectedFormation] = useState(normalizedInitial);
  const [board, setBoard] = useState(() =>
    rolesMode === 'mantra'
      ? buildMantraBoard(activeRosterPurchases, normalizedInitial)
      : buildClassicBoard(activeRosterPurchases, normalizedInitial)
  );
  const [swapOverlay, setSwapOverlay] = useState(null);
  const [swappedIds, setSwappedIds] = useState(() => new Set());
  const [showProbabili, setShowProbabili] = useState(false);

  // Forma recente (voti storici) di tutta la lega: se attivata, aggiusta il
  // punteggio usato per scegliere l'undici (vedi applyRecentFormAdjustment) al
  // posto della sola media stagionale. Spento di default: cambia la scelta
  // dell'undici solo se l'utente lo chiede esplicitamente.
  const [useRecentForm, setUseRecentForm] = useState(false);
  const [ratingsById, setRatingsById] = useState(null);
  useEffect(() => {
    if (!useRecentForm || ratingsById) return undefined;
    let cancelled = false;
    getAllPlayerRatingsMap()
      .then((map) => { if (!cancelled) setRatingsById(map); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [useRecentForm, ratingsById]);

  // Chi ha già in rosa quale giocatore (tutte le squadre dell'asta).
  const boughtByPlayerId = useMemo(() => {
    const m = new Map();
    Object.entries(rosterByTeam || {}).forEach(([team, list]) => {
      (list || []).forEach((p) => {
        const id = String(p.playerId ?? p.id ?? '');
        if (id && !m.has(id)) m.set(id, team);
      });
    });
    return m;
  }, [rosterByTeam]);

  // Titolari previsti per ogni squadra di Serie A (dalle probabili formazioni).
  // `bucket` P/D/C/A per ordinare/colorare; `tokens` = ruoli Mantra per la vista
  // Mantra (in un'asta mantra `pl.role` è già il token, es. "Dc/Ds").
  //
  // Da quale campo prendere il ruolo classic (`bucket`):
  //  - Mantra: `pl.role` è un token composito → si parte dal ruolo classic
  //    originale (`pl.originalRole`) per ricavare il reparto.
  //  - Classic con sorgente ruoli FantApp: si usa il ruolo EFFETTIVO
  //    (`pl.role`), già corretto dagli override FantApp (es. Cambiaghi "C",
  //    Noa Lang "A").
  //  - Classic con sorgente ruoli FantaMaster: si lasciano i ruoli originali
  //    della sorgente (`pl.originalRole`), nessuna conversione.
  const probabiliByTeam = useMemo(() => {
    const isMantra = rolesMode === 'mantra';
    const isFantAppClassic = !isMantra
      && String(auction?.playersSource || '').toLowerCase() === 'fantapp';
    const roleRank = { P: 0, D: 1, C: 2, A: 3 };
    const groups = new Map();
    (players || []).forEach((pl) => {
      const st = playProbabilityById?.[String(pl.id)];
      if (!st || st.status !== 'starter') return;
      const team = pl.team || '—';
      if (!groups.has(team)) groups.set(team, []);
      const roleForBucket = isFantAppClassic
        ? (pl.role || pl.originalRole || 'A')
        : (pl.originalRole || pl.role || 'A');
      groups.get(team).push({
        id: String(pl.id),
        name: pl.name || '—',
        bucket: getClassicBroadRole(roleForBucket),
        tokens: isMantra ? splitRoleTokens(String(pl.role || '')) : [],
        prob: Number(st.prob) || 0,
      });
    });
    return [...groups.entries()]
      .map(([team, list]) => ({
        team,
        players: list.sort((a, b) =>
          (roleRank[a.bucket] - roleRank[b.bucket]) || (b.prob - a.prob) || a.name.localeCompare(b.name)
        ),
      }))
      .sort((a, b) => a.team.localeCompare(b.team));
  }, [players, playProbabilityById, rolesMode, auction?.playersSource]);

  useEffect(() => {
    setSelectedFormation(normalizedInitial);
  }, [normalizedInitial]);

  useEffect(() => {
    const adjustments = { ratingsById: useRecentForm ? ratingsById : null };
    setBoard(
      rolesMode === 'mantra'
        ? buildMantraBoard(activeRosterPurchases, selectedFormation, adjustments)
        : buildClassicBoard(activeRosterPurchases, selectedFormation, adjustments)
    );
    setSwapOverlay(null);
  }, [activeRosterPurchases, selectedFormation, rolesMode, useRecentForm, ratingsById]);

  const currentStats = useMemo(
    () => aggregateStarterStats(getBoardStarters(board, rolesMode), 'current'),
    [board, rolesMode]
  );

  const previousStats = useMemo(
    () => aggregateStarterStats(getBoardStarters(board, rolesMode), 'previous'),
    [board, rolesMode]
  );

  const handleStarterClick = ({ role, id, options }) => {
    if (!id) return;
    setSwapOverlay({ role, id, options });
  };

  const closeSwapOverlay = () => setSwapOverlay(null);

  const handlePickBench = (benchId) => {
    if (!swapOverlay) return;
    const { role, id: starterId } = swapOverlay;

    setBoard((prev) =>
      rolesMode === 'mantra'
        ? swapPlayersWithinMantraBoard(prev, starterId, benchId)
        : swapPlayersWithinClassicBoard(prev, role, starterId, benchId)
    );

    setSwapOverlay(null);
    setSwappedIds(new Set([starterId, benchId]));
    window.setTimeout(() => setSwappedIds(new Set()), 500);
  };

  const swapOverlayStarter = useMemo(() => {
    if (!swapOverlay) return null;
    const starters = getBoardStarters(board, rolesMode);
    return starters.find((p) => String(p.id) === String(swapOverlay.id)) || null;
  }, [swapOverlay, board, rolesMode]);

  const swapOverlayCandidates = useMemo(() => {
    if (!swapOverlay) return [];

    if (rolesMode === 'mantra') {
      const options = swapOverlay.options || [];
      return (board.bench || []).filter((player) =>
        slotMatchesPlayer(player.roleTokens || getPlayerMantraRoles(player), options)
      );
    }

    return board.benchByRole?.[swapOverlay.role] || [];
  }, [swapOverlay, board, rolesMode]);

  const isMantraMode = rolesMode === 'mantra';

  const activePreset = useMemo(() => {
    if (isMantraMode) {
      return MANTRA_PRESETS[selectedFormation] || MANTRA_PRESETS['343'];
    }

    return CLASSIC_PRESETS[selectedFormation] || CLASSIC_PRESETS['4-3-3'];
  }, [isMantraMode, selectedFormation]);

  const mantraPreset = isMantraMode ? activePreset : MANTRA_PRESETS['343'];
  const classicPreset = !isMantraMode ? activePreset : CLASSIC_PRESETS['4-3-3'];

  const mantraBenchGroups = isMantraMode
    ? buildMantraBenchGroups(board.bench || [], mantraPreset?.rows?.map((r) => r.key) || MANTRA_ROW_ORDER)
    : null;

  const classicBenchGroups = !isMantraMode ? board.benchByRole || {} : null;

  return (
    <section className="formation-page">
      <article className="auction-panel glass formation-panel">
        <div className="panel-head formation-panel__head">
          <div>
            <h2>Formazione</h2>
            <span>Scegli il modulo, guarda il tuo miglior undici e scambia i titolari con la panchina.</span>
          </div>

          <div className="formation-panel__actions">
            <AppTooltip
              as="button"
              type="button"
              className={`formation-prob-btn formation-form-btn${useRecentForm ? ' is-active' : ''}`}
              onClick={() => setUseRecentForm((v) => !v)}
              content="Aggiusta il punteggio di ogni giocatore in base alle ultime giornate giocate, non solo alla media stagionale: chi è in un buon momento sale, chi è in calo scende."
            >
              <TrendingUp size={15} />
              Forma recente
            </AppTooltip>

            <button
              type="button"
              className="formation-prob-btn"
              onClick={() => setShowProbabili(true)}
              title="Probabili titolari di ogni squadra di Serie A"
            >
              <Users size={15} />
              Probabili titolari
            </button>

            <label className="formation-team-select">
              <span>Squadra</span>
              <select
                className="market-sort"
                value={selectedTeamName}
                onChange={(e) => onSelectedTeamNameChange?.(e.target.value)}
              >
                {(teamNames || []).map((team) => (
                  <option key={team} value={team}>
                    {team}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        <div className="formation-modules">
          {rolesMode === 'mantra'
            ? MANTRA_OPTIONS.map((formation) => (
              <button
                key={formation}
                type="button"
                className={selectedFormation === formation ? 'is-active' : ''}
                onClick={() => setSelectedFormation(formation)}
              >
                {formation}
              </button>
            ))
            : CLASSIC_OPTIONS.map((formation) => (
              <button
                key={formation}
                type="button"
                className={selectedFormation === formation ? 'is-active' : ''}
                onClick={() => setSelectedFormation(formation)}
              >
                {formation}
              </button>
            ))}
        </div>

        <div className="formation-pitch">
          <div className="formation-pitch__header">
            <div>
              <h3>Campo</h3>
              <span>{isMantraMode ? mantraPreset?.label : selectedFormation}</span>
            </div>
          </div>

          <div className="formation-pitch__rows">
            {rolesMode === 'mantra'
              ? (board.rows || []).map((row) => {
                const starters = row.map((slot) => slot.player).filter(Boolean);
                const required = row.length;
                const missing = Math.max(required - starters.length, 0);

                return (
                  <PitchRow
                    key={row[0]?.rowKey || Math.random()}
                    row={{
                      key: row[0]?.rowKey || 'A',
                      title: row[0]?.rowTitle || '—',
                      subtitle: row[0]?.rowSubtitle || '',
                    }}
                    players={starters}
                    required={required}
                    missing={missing}
                    slots={row}
                    onStarterClick={handleStarterClick}
                    rolesMode={rolesMode}
                    playerCategoriesById={playerCategoriesById}
                    playProbabilityById={playProbabilityById}
                    swappedIds={swappedIds}
                    openStarterId={swapOverlay?.id}
                  />
                );
              })
              : ROLE_ORDER.map((role) => {
                const starters = board.startingByRole?.[role] || [];
                const required = classicPreset?.[role] || 0;
                const missing = Math.max(required - starters.length, 0);

                return (
                  <PitchRow
                    key={role}
                    row={{
                      key: role,
                      title: getRoleLabel(role),
                      subtitle:
                        role === 'P'
                          ? 'Portiere'
                          : role === 'D'
                            ? 'Difesa'
                            : role === 'C'
                              ? 'Centrocampo'
                              : 'Attacco',
                    }}
                    players={starters}
                    required={required}
                    missing={missing}
                    onStarterClick={handleStarterClick}
                    rolesMode={rolesMode}
                    playerCategoriesById={playerCategoriesById}
                    playProbabilityById={playProbabilityById}
                    swappedIds={swappedIds}
                    openStarterId={swapOverlay?.id}
                  />
                );
              })}
          </div>
        </div>

        <div className="formation-bench">
          <div className="formation-bench__head">
            <div>
              <h3>Panchina</h3>
              <span>Clicca un titolare in campo per aprire le riserve compatibili e fare lo scambio.</span>
            </div>
            <span className="formation-bench__note">
              {rolesMode === 'mantra'
                ? 'Le riserve sono raggruppate per compatibilità.'
                : 'Le riserve sono raggruppate per ruolo base.'}
            </span>
          </div>

          <div className={`formation-bench-grid ${rolesMode === 'mantra' ? 'formation-bench-grid--mantra' : ''}`}>
            {rolesMode === 'mantra'
              ? (mantraPreset?.rows || []).map((row) => {
                const rowKey = row.key;
                const list = mantraBenchGroups?.[rowKey] || [];

                return (
                  <section key={rowKey} className={`formation-bench-group ${getRoleAccentClass(rowKey)}`}>
                    <div className="formation-bench-group__head">
                      <div className="formation-bench-group__title">
                        <span className={`formation-bench-group__swatch formation-swatch--${getRoleAccentGroup(rowKey)}`} />
                        <strong>{getMantraRowLabel(rowKey)}</strong>
                      </div>
                      <span>{list.length}</span>
                    </div>

                    <div className="formation-bench-group__list">
                      {list.length ? (
                        list.map((purchase) => (
                          <FormationPlayerCard
                            key={purchase.id}
                            purchase={purchase}
                            variant="bench"
                            rolesMode={rolesMode}
                            showRoles
                            clickable={false}
                            swapped={swappedIds?.has(purchase.id)}
                            injured={isPlayerInjured(purchase, playerCategoriesById)}
                            playProbability={playProbabilityById?.[String(purchase.id)]}
                          />
                        ))
                      ) : (
                        <div className="formation-bench-group__empty">Nessuna riserva per questo ruolo.</div>
                      )}
                    </div>
                  </section>
                );
              })
              : ROLE_ORDER.map((role) => {
                const list = classicBenchGroups?.[role] || [];

                return (
                  <section key={role} className={`formation-bench-group ${getRoleAccentClass(role)}`}>
                    <div className="formation-bench-group__head">
                      <div className="formation-bench-group__title">
                        <span className={`formation-bench-group__swatch formation-swatch--${getRoleAccentGroup(role)}`} />
                        <strong>{getRoleLabel(role)}</strong>
                      </div>
                      <span>{list.length}</span>
                    </div>

                    <div className="formation-bench-group__list">
                      {list.length ? (
                        list.map((purchase) => (
                          <FormationPlayerCard
                            key={purchase.id}
                            purchase={purchase}
                            variant="bench"
                            rolesMode={rolesMode}
                            showRoles
                            clickable={false}
                            swapped={swappedIds?.has(purchase.id)}
                            injured={isPlayerInjured(purchase, playerCategoriesById)}
                            playProbability={playProbabilityById?.[String(purchase.id)]}
                          />
                        ))
                      ) : (
                        <div className="formation-bench-group__empty">Nessuna riserva per questo ruolo.</div>
                      )}
                    </div>
                  </section>
                );
              })}
          </div>

        </div>

        {isMantraMode && <MantraSubstitutionPanel board={board} />}

        <TeamDistributionSection purchases={activeRosterPurchases} />

        <SwapOverlay
          starter={swapOverlayStarter}
          candidates={swapOverlayCandidates}
          rolesMode={rolesMode}
          playerCategoriesById={playerCategoriesById}
          playProbabilityById={playProbabilityById}
          onPick={handlePickBench}
          onClose={closeSwapOverlay}
        />

        {showProbabili && (
          <ProbabiliOverlay
            teams={probabiliByTeam}
            boughtByPlayerId={boughtByPlayerId}
            rolesMode={rolesMode}
            onClose={() => setShowProbabili(false)}
          />
        )}

        <div className="formation-stats">
          <div className="formation-stats__split">
            <section className="formation-stats-panel">
              <div className="formation-stats-panel__head">
                <div>
                  <h3>Statistiche titolari - anno corrente</h3>
                  <span>Calcolate sui soli giocatori schierati nel campetto</span>
                </div>
              </div>

              <div className="formation-stats-grid">
                <FormationStatCard label="Media voto" value={currentStats.avgMark !== null ? fmt(currentStats.avgMark) : '—'} />
                <FormationStatCard
                  label="Media fanta voto"
                  value={currentStats.avgFMark !== null ? fmt(currentStats.avgFMark) : '—'}
                />
                <FormationStatCard label="Gol totali" value={currentStats.totalGoals !== null ? currentStats.totalGoals : '—'} />
                <FormationStatCard
                  label="Assist totali"
                  value={currentStats.totalAssists !== null ? currentStats.totalAssists : '—'}
                />
                <FormationStatCard label="Partite medie" value={currentStats.avgCaps !== null ? fmt(currentStats.avgCaps) : '—'} />
              </div>
            </section>

            <section className="formation-stats-panel">
              <div className="formation-stats-panel__head">
                <div>
                  <h3>Statistiche titolari - anno precedente</h3>
                  <span>Visibili solo se disponibili nei dati del giocatore</span>
                </div>
              </div>

              <div className="formation-stats-grid">
                <FormationStatCard
                  label="Media voto"
                  value={previousStats.hasAnyData && previousStats.avgMark !== null ? fmt(previousStats.avgMark) : '—'}
                />
                <FormationStatCard
                  label="Media fanta voto"
                  value={previousStats.hasAnyData && previousStats.avgFMark !== null ? fmt(previousStats.avgFMark) : '—'}
                />
                <FormationStatCard
                  label="Gol totali"
                  value={previousStats.hasAnyData && previousStats.totalGoals !== null ? previousStats.totalGoals : '—'}
                />
                <FormationStatCard
                  label="Assist totali"
                  value={previousStats.hasAnyData && previousStats.totalAssists !== null ? previousStats.totalAssists : '—'}
                />
                <FormationStatCard
                  label="Partite medie"
                  value={previousStats.hasAnyData && previousStats.avgCaps !== null ? fmt(previousStats.avgCaps) : '—'}
                />
              </div>
            </section>
          </div>
        </div>
      </article>
    </section>
  );
}